import React, {useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectEvent} from 'helpers/hooks/utils';
import AccordionSummary from 'components/atoms/Accordions/AccordionSummary/AccordionSummary';
import Icon from 'components/atoms/Icons/Icon/Icon';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import AccordionDetails from 'components/atoms/Accordions/AccordionDetails/AccordionDetails';
import Typography, {Span} from 'components/atoms/Text/Typography/Typography';
import Markdown from 'components/atoms/Formatters/Markdown/Markdown';
import InlineForm from 'components/organisms/Forms/InlineForm/InlineForm';
import dom from 'helpers/dom';
import utils from 'helpers/utils';
import constants from 'helpers/constants';
import StyledTableViewsContent from 'components/organisms/TableViews/TableViewsContent/TableViewsContent.styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import IosShare from '@mui/icons-material/IosShare';
import Delete from '@mui/icons-material/Delete';
import Save from '@mui/icons-material/Save';
import Add from '@mui/icons-material/Add';
import OpenInBrowser from '@mui/icons-material/OpenInBrowser';
import ConfirmDialog from 'components/organisms/Dialogs/ConfirmDialog/ConfirmDialog';
import {useDialogControl} from 'components/organisms/Providers/DialogProvider/DialogProvider';
import ActionButton from 'components/molecules/Buttons/ActionButton/ActionButton';
import ActionIconButton from 'components/molecules/Buttons/ActionIconButton/ActionIconButton';
import ActionChip from 'components/molecules/Chips/ActionChip/ActionChip';
import {useAuthUser} from 'components/organisms/Providers/AuthProvider/AuthProvider';
import InfoOutlined from '@mui/icons-material/InfoOutlined';

const TableViewsContent = React.forwardRef((props, ref) => {
  const {
    view,
    isDirty,
    isExpanded,
    tableViews,
    onShow,
    onShare,
    onSubmit,
    onDelete,
    onCanNotify,
    ...innerProps
  } = useComponentProps(props, 'TableViewsContent');

  const innerRef = useRef(null);
  const formRef = useRef(null);

  const user = useAuthUser();
  const dialogControl = useDialogControl();

  const smDown = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const [error, setError] = useState(null);
  const [dirty, setDirty] = useState(null);
  const [validation, setValidation] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const isNew = view?.viewId === -1;
  const hasChanges = (dirty || isDirty);

  useImperativeHandle(ref, () => innerRef.current);

  const onCanNotifyEvent = useEffectEvent(onCanNotify);
  const viewFields = useMemo(() => {
    const notification = view?.subscriptions?.find((s) => +s.userId === +user?.userId && s.notification)?.notification;
    const allowNotify = onCanNotifyEvent?.(view) ?? true;

    const fields = [];
    fields.push({
      name: 'name',
      label: 'Name',
      type: constants.formFieldTypes.text,
      entity: 'view',
      initial: view?.name,
      required: true,
      FormFieldProps: {
        fullWidth: true,
        variant: smDown ? 'staticLabel' : 'inlineLabel',
        size: 'smallest'
      }
    });

    fields.push({
      name: 'description',
      label: 'Description',
      type: constants.formFieldTypes.richText,
      entity: 'view',
      initial: view?.description,
      required: false,
      FormFieldProps: {
        variant: smDown ? 'staticLabel' : 'inlineLabel',
        size: 'smallest',
        minRows: 2
      }
    });

    fields.push({
      name: 'notification',
      label: 'Notification',
      entity: 'view',
      formGroup: 'notification',
      type: constants.formFieldTypes.switch,
      validation: constants.formFieldValidationTypes.boolean,
      conversion: constants.formFieldConversionTypes.value,
      initial: Boolean(notification ?? false),
      disabled: !allowNotify,
      FormFieldProps: {
        variant: 'inlineLabel',
        size: 'smallest'
      }
    });

    fields.push({
      section: true,
      name: 'notificationInfo',
      formGroup: 'notification',
      type: constants.formSectionTypes.custom,
      component: <ActionIconButton action={{
                                     icon: InfoOutlined,
                                     tooltip: !allowNotify ? 'This view can not be monitored' : 'Monitor this view for changes',
                                   }}
                                   disableRipple={true}
                                   flatBackground={true}
                                   size="smaller" density="dense" variant="outlined"
                                   TooltipProps={{enterDelay: 0}}
                                   IconProps={{size: 'tiny'}} />
    })

    return fields;
  }, [view, onCanNotifyEvent, user?.userId, smDown]);

  const handleExpand = (e, expanded) => {
    setExpanded(expanded);
  }

  const handleEntered = () => {
    utils.retry(() => {
      if (innerRef.current) {
        dom.scrollIntoView(innerRef.current);
      }
    }, 5).then();
  }

  useLayoutEffect(() => {
    setExpanded(isExpanded);
  }, [tableViews.state.searchValue, isExpanded]);

  useEffect(() => {
    setSubmitting(false);
  }, [isNew]);

  const renderViewTitle = (view) => {
    const title = (isNew ? 'Current view (not saved)' : view?.name) ?? '';
    return <Typography className="TableViewsContent-title" variant="subtitle2">
      <Markdown>{title}</Markdown>
    </Typography>
  }

  const handleValidating = useCallback((isValidating, isDirty, hasErrors) => {
    setDirty(isDirty);
    if (hasErrors) {
      setValidation('Please check if all fields have the correct values');
    } else {
      setValidation(null);
    }
  }, []);

  const onSubmitEvent = useEffectEvent(onSubmit);
  const handleSubmit = useCallback((values, actions) => {
    setSubmitting(true);

    utils.asPromise(onSubmitEvent)(view, values)
      .then(() => {
        setError(null);
      })
      .catch(() => {
        setError('Saving view failed');
      })
      .finally(() => {
        actions.setSubmitting(false);
        if (!isNew) {
          setSubmitting(false);
        }
      });
  }, [isNew, onSubmitEvent, view]);

  const renderView = () => {
    return <InlineForm ref={formRef}
                       className="TableViewsContent-form"
                       fields={viewFields}
                       onValidating={handleValidating}
                       onSubmit={handleSubmit} />
  }

  const onShowEvent = useEffectEvent(onShow);
  const onShareEvent = useEffectEvent(onShare);
  const onDeleteEvent = useEffectEvent(onDelete);
  const titleActions = useMemo(() => {
    const actions = [];
    if (!expanded) {
      actions.push({
        label: !smDown ? 'Open view' : 'Open',
        tooltip: isNew ? 'Save the view first' : null,
        icon: OpenInBrowser,
        auth: isNew ? null : utils.createAuth({attribute: 'view.read', meta: {view}}),
        onClick: (e) => {
          onShowEvent?.(e, view);
          e.preventDefault();
        },
        ChipProps: {
          showTooltip: false,
          disabled: isNew
        },
        ActionChipProps: {
          size: 'medium',
          color: 'primary',
          variant: 'transparent'
        }
      });
    }

    return actions;
  }, [isNew, expanded, onShowEvent, smDown, view]);

  const leftActions = useMemo(() => {
    const actions = [];
    actions.push({
      label: !smDown ? 'Share' : null,
      tooltip: (isNew || hasChanges) ? 'Save the view first' : (
        !smDown ? null : 'Share view'
      ),
      icon: IosShare,
      auth: utils.createAuth({attribute: 'view.share', meta: {view}}),
      onClick: () => {
        onShareEvent?.(null, view);
      },
      ButtonProps: {
        disabled: isNew || hasChanges || submitting
      },
      ActionButtonProps: {
        size: 'small',
        color: 'primary',
        variant: 'text',
      },
      IconButtonProps: {
        disabled: hasChanges || submitting
      },
      ActionIconButtonProps: {
        size: 'smaller',
        color: 'primary',
        variant: 'transparent',
        density: 'normal',
        IconProps: {
          size: 'smaller'
        }
      },
    });

    return actions;
  }, [isNew, submitting, smDown, hasChanges, view, onShareEvent]);

  const rightActions = useMemo(() => {
    const actions = [];

    if (!isNew) {
      actions.push({
        label: !smDown ? 'Remove' : null,
        tooltip: !smDown ? null : 'Remove view',
        icon: Delete,
        auth: utils.createAuth({attribute: 'view.delete', meta: {view}}),
        onClick: (e) => {
          const handleConfirm = () => {
            setExpanded(false);

            utils.asPromise(onDeleteEvent)(null, view)
              .catch(() => {
                setExpanded(true);
                setError('Removing view failed');
              });
          }

          dialogControl.show(<ConfirmDialog question="Are you sure you want to remove this view?"
                                            explanation="The view will only be removed for you"
                                            onConfirm={handleConfirm}
                                            ConfirmButtonProps={{
                                              children: 'Remove view',
                                              startIcon: <Icon icon={Delete} />,
                                              color: 'error'
                                            }} />, true);

          e.preventDefault();
        },
        ButtonProps: {
          disabled: submitting
        },
        ActionButtonProps: {
          size: 'small',
          color: 'error',
          variant: 'contained',
        },
        IconButtonProps: {
          disabled: submitting
        },
        ActionIconButtonProps: {
          size: 'smaller',
          color: 'error',
          variant: 'contained',
          density: 'normal',
          IconProps: {
            size: 'smaller'
          }
        },
      });
    }

    actions.push({
      label: !smDown ? (isNew ? 'Create view' : (hasChanges ? 'Save view' : 'Open view')) : null,
      tooltip: !smDown ? null : (isNew ? 'Create view' : (hasChanges ? 'Save view' : 'Open view')),
      icon: isNew ? Add : (hasChanges ? Save : OpenInBrowser),
      auth: isNew ? utils.createAuth({attribute: 'view.create', meta: {}}) :
        utils.createAuth({attribute: 'view.update', meta: {view}}),
      onClick: (e) => {
        if (isNew || hasChanges) {
          formRef.current?.submit();
        } else {
          onShowEvent?.(e, view);
        }

        e.preventDefault();
      },
      ButtonProps: {
        type: 'submit',
        disabled: submitting
      },
      ActionButtonProps: {
        size: 'small',
        color: isNew ? 'success' : 'primary',
        variant: 'contained',
      },
      IconButtonProps: {
        type: 'submit',
        disabled: submitting
      },
      ActionIconButtonProps: {
        size: 'smaller',
        color: isNew ? 'success' : 'primary',
        variant: 'contained',
        density: 'normal',
        IconProps: {
          size: 'smaller'
        }
      },
    });

    return actions;
  }, [isNew, hasChanges, submitting, smDown, view, onShowEvent, onDeleteEvent, dialogControl]);

  const renderButtons = (actions) => {
    return actions.map((action, idx) => {
      if (action.label) {
        return <ActionButton key={idx}
                             action={action}/>
      } else {
        return <ActionIconButton key={idx}
                                 action={action}/>
      }
    });
  }

  const renderChips = (actions) => {
    return actions.map((action, idx) => {
      return <ActionChip key={idx}
                         action={action}/>
    });
  }

  const renderFooter = () => {
    return <Box className="TableViewsContent-footer">
      <Box className="TableViewsContent-footer-left">
        {renderButtons(leftActions)}
      </Box>
      <Box className="TableViewsContent-footer-center">
        {(error ?? validation) ? <Span color="error">{error ?? validation}</Span> : null}
      </Box>
      <Box className="TableViewsContent-footer-right">
        {renderButtons(rightActions)}
      </Box>
    </Box>
  }

  return <StyledTableViewsContent ref={innerRef} {...innerProps}
                                  expanded={expanded}
                                  onChange={handleExpand}
                                  elevation={0}
                                  density="denser"
                                  square={true}
                                  disableGutters
                                  TransitionProps={{
                                    unmountOnExit: true,
                                    onEntered: handleEntered
                                  }}>
    <AccordionSummary expandIcon={<Icon icon={ArrowDropDown} />}>
      {renderViewTitle(view)}
      {renderChips(titleActions)}
    </AccordionSummary>
    <AccordionDetails>
      {renderView(view)}
      {renderFooter()}
    </AccordionDetails>
  </StyledTableViewsContent>
});

TableViewsContent.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  view: PropTypes.object,
  isDirty: PropTypes.bool,
  isExpanded: PropTypes.bool,
  tableViews: PropTypes.object,
  onShow: PropTypes.func,
  onShare: PropTypes.func,
  onSubmit: PropTypes.func,
  onDelete: PropTypes.func,
  onCanNotify: PropTypes.func
};

export default TableViewsContent;
